/**
 * Custom hook for managing monitoring hierarchy and states
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export const useMonitoring = (sessionId, labId) => {
  const [monitoringHierarchy, setMonitoringHierarchy] = useState([]);
  const [monitoringStates, setMonitoringStates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch monitoring hierarchy for lab
  const fetchHierarchy = useCallback(async () => {
    if (!labId) return;

    try {
      setLoading(true);
      const response = await api.get(`/api/monitoring/hierarchy/lab/${labId}/tree`);
      setMonitoringHierarchy(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching monitoring hierarchy:', err);
      setError('Failed to load monitoring hierarchy');
      setMonitoringHierarchy([]);
    } finally {
      setLoading(false);
    }
  }, [labId]);

  // Fetch current monitoring states for session
  const fetchStates = useCallback(async () => {
    if (!sessionId) return;

    try {
      const response = await api.get(`/api/monitoring/state/session/${sessionId}`);
      const statesMap = {};
      response.data.forEach(state => {
        statesMap[state.monitoring_hierarchy_id] = state;
      });
      setMonitoringStates(statesMap);
    } catch (err) {
      console.error('Error fetching monitoring states:', err);
    }
  }, [sessionId]);

  // Initialize monitoring states for new session
  const initializeStates = useCallback(async () => {
    if (!sessionId || !labId) return;

    try {
      const response = await api.post(
        `/api/monitoring/state/session/${sessionId}/initialize`,
        null,
        { params: { lab_id: labId } }
      );
      const statesMap = {};
      response.data.forEach(state => {
        statesMap[state.monitoring_hierarchy_id] = state;
      });
      setMonitoringStates(statesMap);
    } catch (err) {
      console.error('Error initializing monitoring states:', err);
    }
  }, [sessionId, labId]);

  // Update monitoring state via API
  const updateMonitoringState = useCallback(async (nodeId, status, statusMessage = null, metricValue = null) => {
    if (!sessionId) return;

    try {
      const response = await api.post(`/api/monitoring/state/session/${sessionId}`, {
        monitoring_hierarchy_id: nodeId,
        status,
        status_message: statusMessage,
        metric_value: metricValue
      });

      // Update local state
      setMonitoringStates(prev => ({
        ...prev,
        [nodeId]: response.data
      }));

      return response.data;
    } catch (err) {
      console.error('Error updating monitoring state:', err);
      throw err;
    }
  }, [sessionId]);

  // Handle WebSocket monitoring update
  const handleWebSocketUpdate = useCallback((data) => {
    if (data.type === 'monitoring_update') {
      setMonitoringStates(prev => ({
        ...prev,
        [data.monitoring_hierarchy_id]: {
          id: prev[data.monitoring_hierarchy_id]?.id,
          lab_session_id: sessionId,
          monitoring_hierarchy_id: data.monitoring_hierarchy_id,
          status: data.status,
          status_message: data.status_message,
          metric_value: data.metric_value,
          updated_by: data.updated_by,
          updated_at: data.updated_at
        }
      }));
    } else if (data.type === 'monitoring_summary') {
      // Handle summary updates if needed
      console.log('Monitoring summary:', data.summary);
    }
  }, [sessionId]);

  // Fetch hierarchy on mount
  useEffect(() => {
    fetchHierarchy();
  }, [fetchHierarchy]);

  // Fetch or initialize states when hierarchy is loaded
  useEffect(() => {
    if (monitoringHierarchy.length > 0 && sessionId) {
      fetchStates().catch(() => {
        // If no states exist, initialize them
        initializeStates();
      });
    }
  }, [monitoringHierarchy, sessionId, fetchStates, initializeStates]);

  return {
    monitoringHierarchy,
    monitoringStates,
    loading,
    error,
    updateMonitoringState,
    handleWebSocketUpdate,
    refetch: fetchStates
  };
};
